import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_ROLES } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/common/Header';
import { ROUTE_PERMISSIONS } from '../config/permissionsMap';
import { ROLES_CAN_SEE_EXERCISES_ALWAYS } from '../config/permissionsList';
import { 
  HomeIcon, 
  UsersIcon, 
  TrophyIcon, 
  BarChart3Icon, 
  SettingsIcon,
  UserIcon,
  ShieldIcon,
  BuildingIcon,
  GlobeIcon,
  MapIcon,
  GraduationCapIcon,
  MapPinIcon,
  Building2Icon,
  FolderIcon,
  ChevronDownIcon,
  LayersIcon,
  BookOpenIcon,
  TargetIcon,
  Gamepad2Icon,
  ZapIcon
} from 'lucide-react';

const AdminLayout = () => {
  const { t } = useTranslation();
  const { user, hasAnyPermission, hasAnyRole } = useAuth();
  const canSeeGamesManagement = user?.role === USER_ROLES.SUPER_ADMIN;
  const canSeeChallengesManagement = hasAnyRole([USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]);
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [openSubmenu, setOpenSubmenu] = React.useState({});

  // قسم التمارين اليومية: يظهر فقط للأدمن والأدمن المتقدم (مخفى عن جميع المستخدمين أقل من أدمين)
  const canSeeDailyExercises = hasAnyRole(ROLES_CAN_SEE_EXERCISES_ALWAYS);
  // Toggle submenu
  const toggleSubmenu = (itemName) => {
    setOpenSubmenu(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  // تحديد عنوان الصفحة حسب المسار (الأكثر تحديداً أولاً؛ لا تستخدم path.includes('/dashboard') لكل /dashboard/*)
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/flash-anzan-level-statistics')) return t('dashboard.stats.flashAnzanLevelTitle');
    if (path.includes('/flash-cards-level-statistics')) return t('dashboard.stats.flashCardsLevelTitle');
    /* تقارير — يجب أن تسبق أي شرط عام مثل /dashboard أو /students */
    if (path.includes('/reports/games/students')) return t('reports.gamesApi.studentSessionsPageTitle');
    if (path.includes('/reports/games')) return t('reports.tabGamesShort');
    if (path.includes('/reports/challenges/students')) return t('reports.challengesApi.studentSubmissionsPageTitle');
    if (path.includes('/reports/challenges')) return t('reports.tabChallengesShort');
    if (path.includes('/reports/flash-cards/students')) return t('reports.flashCardsApi.studentSessionsPageTitle');
    if (path.includes('/reports/flash-cards')) return t('reports.tabFlashCardsShort');
    if (path.includes('/reports/daily-exercises-ranking')) return t('reports.tabDailyRankingShort');
    if (path.includes('/reports/users')) return t('reports.tabUsersShort');
    if (path.includes('/reports/flash-anzan/students')) return t('reports.flashAnzanApi.studentSessionsPageTitle');
    if (path.includes('/reports/flash-anzan')) return t('reports.tabFlashAnzanShort');
    if (path.includes('/reports/daily-exercises/students')) return t('reports.dailyExercisesApi.studentSessionsPageTitle');
    if (path.includes('/reports/daily-exercises')) return t('reports.tabDailyExercisesShort');
    if (path.includes('/reports')) return t('navigation.reports');
    if (path.includes('/profile')) return t('navigation.profile');
    if (path.includes('/students')) return t('navigation.students');
    if (path.includes('/competitions')) return t('navigation.competitions');
    if (path.includes('/settings')) return t('navigation.settings');
    if (path.includes('/games')) return t('navigation.games');
    if (path.includes('/daily-exercises')) return t('navigation.dailyExercises');
    if (path.includes('/challenges')) return t('navigation.challenges');
    if (path === '/dashboard' || path === '/dashboard/') return t('navigation.dashboard');
    return t('navigation.dashboard');
  };

  const getNavigationItems = () => {
    const items = [
      { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon, permission: null },
      { name: t('navigation.competitions'), href: '/dashboard/competitions', icon: TrophyIcon, permission: null },
      {
        name: t('navigation.challenges'),
        href: '/dashboard/challenges',
        icon: TargetIcon,
        permission: null,
        visibility: () => canSeeChallengesManagement
      },
      {
        name: t('navigation.games'),
        href: '/dashboard/games',
        icon: Gamepad2Icon,
        permission: null,
        visibility: () => canSeeGamesManagement
      },
      { name: t('navigation.dailyExercises'), href: '/dashboard/daily-exercises', icon: BookOpenIcon, permission: ROUTE_PERMISSIONS['daily-exercises'], permissionMode: 'any', visibility: () => canSeeDailyExercises },
      { 
        name: t('navigation.organisation'), 
        href: '/dashboard/countries', 
        icon: BuildingIcon,
        permission: ROUTE_PERMISSIONS.countries,
        children: [
          { name: t('navigation.countries'), href: '/dashboard/countries', icon: GlobeIcon, permission: ROUTE_PERMISSIONS.countries },
          { name: t('navigation.regions'), href: '/dashboard/regions', icon: MapIcon, permission: ROUTE_PERMISSIONS.regions },
          { name: t('navigation.academies'), href: '/dashboard/academies', icon: GraduationCapIcon, permission: ROUTE_PERMISSIONS.academies },
          { name: t('navigation.districts'), href: '/dashboard/districts', icon: MapPinIcon, permission: ROUTE_PERMISSIONS.districts },
          { name: t('navigation.institutions'), href: '/dashboard/institutions', icon: Building2Icon, permission: ROUTE_PERMISSIONS.institutions },
          { name: t('navigation.departments'), href: '/dashboard/departments', icon: FolderIcon, permission: ROUTE_PERMISSIONS.departments },
          { name: t('navigation.learningLevels'), href: '/dashboard/learning-levels', icon: LayersIcon, permission: ROUTE_PERMISSIONS['learning-levels'] }
        ]
      },
      { 
        name: t('navigation.users'), 
        href: '/dashboard/users', 
        icon: UserIcon,
        permission: ROUTE_PERMISSIONS.users,
        children: [
          { name: t('navigation.allUsers'), href: '/dashboard/users', icon: UsersIcon, permission: ROUTE_PERMISSIONS.users },
          { name: t('navigation.students'), href: '/dashboard/students', icon: GraduationCapIcon, permission: ROUTE_PERMISSIONS.students, permissionMode: 'any' }
        ]
      },
      {
        name: t('navigation.reports'),
        href: '/dashboard/reports',
        icon: BarChart3Icon,
        permission: ROUTE_PERMISSIONS.reports,
        children: [
          { name: t('reports.tabDailyRankingShort'), href: '/dashboard/reports/daily-exercises-ranking', icon: BookOpenIcon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabUsersShort'), href: '/dashboard/reports/users', icon: UsersIcon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabDailyExercisesShort'), href: '/dashboard/reports/daily-exercises', icon: BarChart3Icon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabFlashAnzanShort'), href: '/dashboard/reports/flash-anzan', icon: ZapIcon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabFlashCardsShort'), href: '/dashboard/reports/flash-cards', icon: LayersIcon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabGamesShort'), href: '/dashboard/reports/games', icon: Gamepad2Icon, permission: ROUTE_PERMISSIONS.reports },
          { name: t('reports.tabChallengesShort'), href: '/dashboard/reports/challenges', icon: TargetIcon, permission: ROUTE_PERMISSIONS.reports }
        ]
      },
      { name: t('navigation.settings'), href: '/dashboard/settings', icon: SettingsIcon, permission: ROUTE_PERMISSIONS.settings },
      { name: t('navigation.profile'), href: '/dashboard/profile', icon: UserIcon, permission: ROUTE_PERMISSIONS.profile },
    ];

    const filterByPermission = (item) => {
      if (item.visibility && !item.visibility()) return false;
      if (!item.permission) return true;
      const perm = Array.isArray(item.permission) ? item.permission : [item.permission];
      return hasAnyPermission(perm);
    };

    return items.filter(filterByPermission).map((item) => {
      if (item.children) {
        const filteredChildren = item.children.filter(filterByPermission);
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }
      return item;
    }).filter(Boolean);
  };

  const navigationItems = getNavigationItems();

  return (
    <div className={`min-h-screen ${isRTL ? 'font-arabic' : ''}`}>
      {/* Sidebar */}
      <div className={`fixed inset-y-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        isRTL ? 'right-0' : 'left-0'
      } ${sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 bg-primary-600">
            <ShieldIcon className="h-8 w-8 text-white" />
            <span className={`text-xl font-bold text-white ${isRTL ? 'mr-2' : 'ml-2'}`}>eSoroban Admin</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || (item.children && item.children.some(child => location.pathname === child.href));
              
              if (item.children) {
                const isOpen = openSubmenu[item.name];
                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 hover:text-primary-600 ${
                        isActive ? 'bg-gray-100 text-primary-600' : 'text-gray-700'
                      } ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      <div className="flex items-center">
                        <Icon className={`w-5 h-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                        {item.name}
                      </div>
                      <ChevronDownIcon 
                        className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} space-y-1`}>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = location.pathname === child.href;
                          return (
                            <button
                              key={child.name}
                              onClick={() => {
                                navigate(child.href);
                                setSidebarOpen(false);
                              }}
                              className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 hover:text-primary-600 ${
                                isChildActive
                                  ? `bg-primary-50 text-primary-600 ${isRTL ? 'border-l-2 border-primary-600' : 'border-r-2 border-primary-600'}`
                                  : 'text-gray-600'
                              }`}
                            >
                              <ChildIcon className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              {child.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 hover:text-primary-600 ${
                    isActive
                      ? `bg-primary-50 text-primary-600 ${isRTL ? 'border-l-2 border-primary-600' : 'border-r-2 border-primary-600'}`
                      : 'text-gray-700'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Project Info */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <ShieldIcon className="h-5 w-5 text-primary-600" />
                <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'mr-2' : 'ml-2'}`}>
                  eSoroban Learning
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Version 1.0.0
              </p>
              <p className="text-xs text-gray-400 mt-1">
                © 2024 All Rights Reserved
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content column */}
      <div
        className={`min-h-screen bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(99,102,241,0.12),transparent),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(16,185,129,0.08),transparent),linear-gradient(to_bottom,#f8fafc,#f1f5f9_50%,#eef2ff)] ${isRTL ? 'lg:pr-64' : 'lg:pl-64'}`}
      >
        {/* Header */}
        <Header
          pageTitle={getPageTitle()}
          onToggleSidebar={() => setSidebarOpen((o) => !o)}
          sidebarOpen={sidebarOpen}
        />

        <main className="py-6">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
